import React, { useState } from 'react'
import { trpc } from '@klear/ui/lib/trpc'
import { Input } from '@klear/ui/components/input'
import { Button } from '@klear/ui/components/button'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { PERMISSION_ACTION_EDIT, PERMISSION_ENTITY_SOURCE_DOCUMENT } from '@klear/constants'

const PANEL_CLASS = 'flex flex-col gap-2 rounded-md border border-dashed border-border p-3 bg-muted/50'

const TraderSenderDomainPanel: React.FC<{ traderId: number }> = ({ traderId }) => {
  const { user } = useAuth()
  const canEdit = user.hasPermission(PERMISSION_ENTITY_SOURCE_DOCUMENT, PERMISSION_ACTION_EDIT)
  const [draft, setDraft] = useState<string | null>(null)
  const utils = trpc.useUtils()

  const current = trpc.inboundEmail.senderDomain.useQuery({ traderId })
  const save = trpc.inboundEmail.setSenderDomain.useMutation({
    onSuccess: (result) => {
      setDraft(null)
      utils.inboundEmail.senderDomain.setData({ traderId }, result)
    }
  })

  const senderDomain = current.data?.senderDomain ?? null
  const isEditing = draft !== null

  const submit = () => {
    if (draft === null) return
    save.mutate({ traderId, senderDomain: draft.trim() === '' ? null : draft })
  }

  return (
    <div className={PANEL_CLASS}>
      <span className="text-sm text-muted-foreground">Email sender domain</span>
      <span className="text-xs text-muted-foreground">PDFs emailed from this domain are filed under this trader.</span>

      {!isEditing && (
        <div className="flex flex-wrap items-center gap-2">
          {current.isLoading ? (
            <span className="text-xs text-muted-foreground">Loading…</span>
          ) : current.isError ? (
            <span className="text-xs text-destructive">{current.error.message}</span>
          ) : senderDomain ? (
            <span className="font-mono text-sm">{senderDomain}</span>
          ) : (
            <span className="text-sm text-muted-foreground">not set</span>
          )}
          {canEdit && current.isSuccess && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={() => setDraft(senderDomain ?? '')}
            >
              edit
            </Button>
          )}
        </div>
      )}

      {isEditing && (
        <div className="flex items-center gap-2">
          <Input
            type="text"
            placeholder="example.com"
            aria-label="Email sender domain"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submit()
              }
            }}
            autoFocus
          />
          <Button type="button" size="sm" disabled={save.isPending} onClick={submit}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setDraft(null)
              save.reset()
            }}
          >
            Cancel
          </Button>
        </div>
      )}

      {save.isError && <p className="text-xs text-destructive">{save.error.message}</p>}
    </div>
  )
}

export default TraderSenderDomainPanel
